// Before the game: sign in (guest, Discord, Google), pick a character, or make a new one.
import { CAST, KIT, castLook, lookFromRecipe, randomKit, HATS, FACE_HAIR, type KitLook } from '../../../shared/kit.js';
import { FACTIONS, factionById, type Faction } from '../../../shared/factions.js';
import { sanitizeLook, type Look } from '../../../shared/look.js';
import { checkName } from '../../../shared/names.js';
import { store } from './hud.js';

const $ = <T extends HTMLElement = HTMLElement>(id: string) => document.getElementById(id) as T;

export interface CharacterSummary { id: number; name: string; faction: number; look: Look; lastSeen: string | null }
interface BetaStatus { access: boolean; owner: boolean; email: string | null; signup: { email: string; status: string; at: string } | null }
interface Me { signedIn: boolean; id: number; guest: boolean; linked: { provider: string; label: string | null }[]; beta?: BetaStatus }
interface Config { providers: string[]; factions: Faction[]; maxCharacters: number; beta?: boolean; betaCode?: boolean }
interface Signup { id: number; email: string; note: string; status: 'pending' | 'approved'; at: string; accountId: number | null; label: string | null }
const cap = (p: string) => p[0].toUpperCase() + p.slice(1);
const when = (iso: string) => { try { return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }); } catch { return ''; } };

const NAMES = ['Pip', 'Juniper', 'Bramble', 'Wren', 'Clover', 'Rook', 'Maple', 'Tansy', 'Moss', 'Fennel', 'Olive', 'Sorrel'];
/** A look the server will accept, or null. */
export const lookFrom = (raw: unknown): Look | null => sanitizeLook(raw);

const STYLES: [string, string][] = [['short', 'Short'], ['cowlick', 'Cowlick'], ['receding', 'Receding'], ['long', 'Long'], ['bob', 'Bob'], ['ponytail', 'Ponytail'], ['braids', 'Braids']];
const HAT_PICKS: [string, string][] = [['', 'None'], ['beanie', 'Beanie'], ['bowler', 'Bowler'], ['kepi', 'Cap'], ['bighat', 'Big hat'], ['toque', 'Chef'], ['slouch', 'Slouch']];
const FACE_PICKS: [string, string][] = [['', 'None'], ['mustache', 'Mustache'], ['whiskers', 'Whiskers'], ['muttonchops', 'Mutton chops']];

async function api<T>(path: string, method = 'GET', body?: unknown): Promise<{ status: number; data: T }> {
  const r = await fetch(path, { method, credentials: 'same-origin', headers: { 'X-TT': '1', ...(body ? { 'Content-Type': 'application/json' } : {}) }, body: body ? JSON.stringify(body) : undefined });
  let data: T; try { data = await r.json(); } catch { data = {} as T; }
  return { status: r.status, data };
}

export class Gate {
  private config: Config = { providers: ['guest'], factions: FACTIONS, maxCharacters: 8 };
  private me: Me | null = null;
  private chars: CharacterSummary[] = [];
  onPlay: (c: CharacterSummary) => void = () => {};

  constructor(private drawSprite: (canvas: HTMLCanvasElement, look: Look) => void) {
    $('newCharBtn').addEventListener('click', () => this.showCreate());
    $('backBtn').addEventListener('click', () => this.showChars());
    $('signOutBtn').addEventListener('click', () => void this.signOut());
    this.setupCreate();
    this.setupBeta();
  }

  async start(): Promise<void> {
    const q = new URLSearchParams(location.search);
    if (q.get('signin')) { $('signinErr').textContent = q.get('signin') === 'banned' ? 'This account is suspended.' : 'Sign-in did not complete. Try again.'; history.replaceState(null, '', location.pathname); }
    try { this.config = (await api<Config>('/api/config')).data; } catch { /* offline: show the guest button anyway */ }
    await this.refreshMe();
    if (this.me && !this.allowed) this.showBeta();
    else if (this.me) await this.showChars();
    else if (this.config.beta) this.showBeta();
    else this.showSignin();
  }

  private async refreshMe(): Promise<void> {
    const me = await api<Me>('/api/me').catch(() => ({ status: 0, data: null as unknown as Me }));
    this.me = me.status === 200 && me.data?.signedIn ? me.data : null;
  }
  /** In the closed beta, can this account play? */
  private get allowed(): boolean { return !this.config.beta || !!this.me?.beta?.access; }

  private async signOut(): Promise<void> { await api('/auth/logout', 'POST'); this.me = null; if (this.config.beta) this.showBeta(); else this.showSignin(); }

  private screen(id: 'signin' | 'chars' | 'createForm' | 'beta' | 'betaAdmin'): void {
    $('gate').hidden = false;
    // locked: nothing of the game shows until you're let in
    $('gate').classList.toggle('locked', id === 'beta' || (!!this.config.beta && id === 'signin'));
    for (const s of ['signin', 'chars', 'createForm', 'beta', 'betaAdmin']) $(s).hidden = s !== id;
  }

  hide(): void { $('gate').hidden = true; $('queueBox').hidden = true; }

  /** Waiting in line: show where you are and roughly how long it'll take. */
  showQueue(position: number, size: number, etaSec: number | null): void {
    this.screen('chars');
    $('queueBox').hidden = false;
    $('queuePos').textContent = `#${position}`;
    const mins = etaSec == null ? null : Math.max(1, Math.round(etaSec / 60));
    $('queueEta').textContent = `${size} in line · ` + (mins == null ? 'waiting for someone to leave' : `about ${mins} minute${mins === 1 ? '' : 's'}`) + '. Keep this tab open.';
    for (const b of document.querySelectorAll<HTMLButtonElement>('#charList .btn')) b.disabled = true;
  }
  hideQueue(): void { $('queueBox').hidden = true; for (const b of document.querySelectorAll<HTMLButtonElement>('#charList .btn')) b.disabled = false; }

  /** How busy the server is, under the character list. */
  async showStatus(): Promise<void> {
    const r = await api<{ players: number; capacity: number; queue: number; layers: Record<string, number[]>; waitSec: number | null }>('/api/status');
    const el = $('serverStatus'); if (r.status !== 200 || !r.data) { el.textContent = ''; return; }
    const d = r.data, layers = Object.values(d.layers).reduce((a, l) => a + l.length, 0), fill = d.players / Math.max(1, d.capacity);
    el.replaceChildren();
    const dot = document.createElement('span'); dot.className = 'dot' + (d.queue || fill >= 1 ? ' full' : fill > 0.8 ? ' busy' : '');
    const text = `${d.players} / ${d.capacity} playing` + (layers > 1 ? ` · ${layers} layers` : '') + (d.queue ? ` · ${d.queue} waiting${d.waitSec ? ` (about ${Math.max(1, Math.round(d.waitSec / 60))} min)` : ''}` : '');
    el.append(dot, text);
  }

  showSignin(): void {
    this.screen('signin');
    const box = $('providers'); box.textContent = '';
    const add = (label: string, cls: string, fn: () => void) => { const b = document.createElement('button'); b.type = 'button'; b.className = 'btn provider ' + cls; b.textContent = label; b.addEventListener('click', fn); box.appendChild(b); };
    if (this.config.providers.includes('discord')) add('Continue with Discord', 'discord', () => location.assign('/auth/discord/start'));
    if (this.config.providers.includes('google')) add('Continue with Google', 'google', () => location.assign('/auth/google/start'));
    add('Play as guest', 'primary', async () => {
      const r = await api('/auth/guest', 'POST');
      if (r.status !== 200) { $('signinErr').textContent = (r.data as { error?: string }).error || 'Could not start a guest session.'; return; }
      await this.refreshMe(); await this.showChars();
    });
  }

  async showChars(error = ''): Promise<void> {
    this.screen('chars');
    $('charsErr').textContent = error;
    const note = $('acctNote');
    const links = this.config.providers.filter(p => p !== 'guest' && !this.me?.linked.some(l => l.provider === p));
    if (this.me?.guest) {
      note.hidden = false;
      note.innerHTML = 'You are playing as a guest on this browser. ';
      if (links.length) {
        note.append('Keep your characters on any device: ');
        links.forEach((p, i) => { const a = document.createElement('a'); a.href = `/auth/${p}/start`; a.textContent = `link ${p[0].toUpperCase() + p.slice(1)}`; note.append(i ? ' or ' : '', a); });
        note.append('.');
      }
    } else if (this.me?.linked.length) { note.hidden = false; note.textContent = `Signed in with ${this.me.linked.map(l => `${l.provider[0].toUpperCase() + l.provider.slice(1)}${l.label ? ' (' + l.label + ')' : ''}`).join(', ')}.`; }
    else note.hidden = true;
    $('betaAdminBtn').hidden = !this.me?.beta?.owner;
    void this.showStatus();
    const r = await api<CharacterSummary[]>('/api/characters');
    if (r.status === 401) { this.me = null; return this.config.beta ? this.showBeta() : this.showSignin(); }
    if (r.status === 403) { await this.refreshMe(); return this.showBeta(); }
    this.chars = Array.isArray(r.data) ? r.data : [];
    const list = $('charList'); list.textContent = '';
    if (!this.chars.length) { const e = document.createElement('p'); e.className = 'empty'; e.textContent = 'No characters yet. Make your first one!'; list.appendChild(e); }
    for (const c of this.chars) {
      const row = document.createElement('div'); row.className = 'char';
      const cv = document.createElement('canvas'); cv.width = 32; cv.height = 34; this.drawSprite(cv, c.look);
      const info = document.createElement('div'), f = factionById(c.faction);
      const nm = document.createElement('div'); nm.className = 'nm'; nm.textContent = c.name;
      const sub = document.createElement('div'); sub.className = 'sub';
      const pip = document.createElement('span'); pip.className = 'pip'; pip.style.background = f?.color ?? '#fff';
      sub.append(pip, f?.name ?? '');
      info.append(nm, sub);
      const acts = document.createElement('div'); acts.className = 'acts';
      const play = document.createElement('button'); play.type = 'button'; play.className = 'btn primary'; play.textContent = 'Play';
      play.addEventListener('click', () => this.onPlay(c));
      const del = document.createElement('button'); del.type = 'button'; del.className = 'btn del'; del.textContent = 'Delete'; del.setAttribute('aria-label', `Delete ${c.name}`);
      del.addEventListener('click', async () => {
        if (!del.classList.contains('sure')) { del.classList.add('sure'); del.textContent = 'Really?'; setTimeout(() => { del.classList.remove('sure'); del.textContent = 'Delete'; }, 3000); return; }
        await api(`/api/characters/${c.id}`, 'DELETE'); await this.showChars();
      });
      acts.append(play, del);
      row.append(cv, info, acts); list.appendChild(row);
    }
    ($('newCharBtn') as HTMLButtonElement).disabled = this.chars.length >= this.config.maxCharacters;
  }

  // ---------- create ----------
  private currentLook: () => Look | null = () => null;
  private look: KitLook = castLook(0);
  private drawPick: () => void = () => {};
  private setupCreate(): void {
    const nameIn = $<HTMLInputElement>('nameIn'), lookIn = $<HTMLSelectElement>('lookIn'), recipeIn = $<HTMLTextAreaElement>('recipeIn'), err = $('createErr');
    CAST.forEach(([name], i) => { const o = document.createElement('option'); o.value = String(i); o.textContent = name; lookIn.appendChild(o); });
    for (const f of FACTIONS) {
      const l = document.createElement('label'), r = document.createElement('input'), pip = document.createElement('span');
      r.type = 'radio'; r.name = 'faction'; r.value = String(f.id);
      pip.className = 'pip'; pip.style.background = f.color;
      l.append(r, pip, f.name); $('factions').appendChild(l);
    }
    this.currentLook = () => {
      const r = recipeIn.value.trim();
      if (r) return lookFromRecipe(r);
      return sanitizeLook(this.look);
    };
    const refresh = () => { const l = this.currentLook(); if (l) this.drawSprite($<HTMLCanvasElement>('preview'), l); this.drawPick(); };
    // quick picks: hair, colours and a hat, straight onto the look
    const host = $('kitPick');
    const row = (label: string) => { const r = document.createElement('div'); r.className = 'krow'; const b = document.createElement('span'); b.textContent = label; const o = document.createElement('div'); o.className = 'kopts'; r.append(b, o); host.append(r); return o; };
    const picks: (() => void)[] = [];
    const swatches = (label: string, key: 'hair' | 'skin' | 'top' | 'bottom', table: Record<string, string>) => {
      const o = row(label);
      for (const [k, hex] of Object.entries(table)) {
        const b = document.createElement('button'); b.type = 'button'; b.className = 'sw'; b.title = k; b.style.background = hex;
        b.addEventListener('click', () => { this.look = { ...this.look, [key]: k }; recipeIn.value = ''; refresh(); });
        o.append(b); picks.push(() => b.classList.toggle('on', this.look[key] === k));
      }
    };
    const chips = (label: string, items: [string, string][], get: () => string, set: (v: string) => void) => {
      const o = row(label);
      for (const [k, name] of items) {
        const b = document.createElement('button'); b.type = 'button'; b.className = 'chip'; b.textContent = name;
        b.addEventListener('click', () => { set(k); recipeIn.value = ''; refresh(); });
        o.append(b); picks.push(() => b.classList.toggle('on', get() === k));
      }
    };
    chips('Hair', STYLES, () => this.look.style, v => { this.look = { ...this.look, style: v }; });
    swatches('Colour', 'hair', KIT.HAIRS);
    swatches('Skin', 'skin', KIT.SKINS);
    swatches('Top', 'top', KIT.TOPS);
    swatches('Trousers', 'bottom', KIT.BOTTOMS);
    const without = (list: string[]) => this.look.wear.filter(w => !list.includes(w));
    chips('Hat', HAT_PICKS, () => this.look.wear.find(w => HATS.includes(w)) ?? '', v => { this.look = { ...this.look, wear: v ? [...without(HATS), v] : without(HATS) }; });
    chips('Face', FACE_PICKS, () => this.look.wear.find(w => FACE_HAIR.includes(w)) ?? '', v => { this.look = { ...this.look, wear: v ? [...without(FACE_HAIR), v] : without(FACE_HAIR) }; });
    this.drawPick = () => { for (const p of picks) p(); };
    lookIn.addEventListener('change', () => { this.look = castLook(Number(lookIn.value)); recipeIn.value = ''; refresh(); });
    $('lookRandom').addEventListener('click', () => { this.look = randomKit(); recipeIn.value = ''; refresh(); });
    recipeIn.addEventListener('input', () => { const l = lookFromRecipe(recipeIn.value); if (l) this.look = l; refresh(); });
    nameIn.addEventListener('input', () => { const c = checkName(nameIn.value); err.textContent = nameIn.value && !c.ok ? c.error : ''; });
    $('createForm').addEventListener('submit', async e => {
      e.preventDefault();
      const look = this.currentLook(), n = checkName(nameIn.value);
      if (!n.ok) { err.textContent = n.error; return; }
      if (!look) { err.textContent = 'That look could not be read. Copy the link from the Character Maker again.'; return; }
      const faction = Number((document.querySelector('input[name=faction]:checked') as HTMLInputElement | null)?.value) || 0;
      if (!faction) { err.textContent = 'Pick a faction.'; return; }
      store.set('tt-look', JSON.stringify(look)); store.set('tt-faction', String(faction));
      const btn = $<HTMLButtonElement>('createBtn'); btn.disabled = true;
      const r = await api<CharacterSummary & { error?: string }>('/api/characters', 'POST', { name: n.name, faction, look });
      btn.disabled = false;
      if (r.status !== 201) { err.textContent = r.data.error || 'Could not create that character.'; return; }
      await this.showChars();
    });
  }

  showCreate(): void {
    this.screen('createForm');
    const nameIn = $<HTMLInputElement>('nameIn'), lookIn = $<HTMLSelectElement>('lookIn');
    $('createErr').textContent = '';
    nameIn.value = NAMES[Math.floor(Math.random() * NAMES.length)] + (Math.random() < 0.5 ? '' : NAMES[Math.floor(Math.random() * NAMES.length)].slice(0, 3).toLowerCase());
    let saved: Look | null = null; try { saved = sanitizeLook(JSON.parse(store.get('tt-look') ?? 'null')); } catch { saved = null; }
    const pick = Math.floor(Math.random() * CAST.length);
    this.look = saved ?? castLook(pick); lookIn.value = String(saved ? 0 : pick); $<HTMLTextAreaElement>('recipeIn').value = '';
    this.drawPick();
    const fid = Number(store.get('tt-faction')) || FACTIONS[Math.floor(Math.random() * FACTIONS.length)].id;
    document.querySelectorAll<HTMLInputElement>('input[name=faction]').forEach(r => { r.checked = Number(r.value) === fid; });
    const l = this.currentLook(); if (l) this.drawSprite($<HTMLCanvasElement>('preview'), l);
    nameIn.focus(); nameIn.select();
  }

  // ---------- the closed beta ----------
  private castDrawn = false;
  /** The beta screen: sign in, ask to join, or enter an invite code. Signed in without access: where your request stands. */
  showBeta(note = ''): void {
    this.screen('beta');
    if (!this.castDrawn) {
      this.castDrawn = true;
      const host = $('betaCast');
      for (let i = 0; i < 6; i++) { const cv = document.createElement('canvas'); cv.width = 32; cv.height = 34; this.drawSprite(cv, sanitizeLook(i < 3 ? castLook(i * 2) : randomKit())!); host.append(cv); }
    }
    const me = this.me, b = me?.beta;
    $('betaErr').textContent = note;
    // who you are, if signed in
    const who = $('betaWho');
    if (me) {
      const how = me.linked.length ? me.linked.map(l => `${cap(l.provider)}${l.label ? ' (' + l.label + ')' : ''}`).join(', ') : 'a guest account';
      who.hidden = false; who.textContent = `Signed in with ${how}. This account doesn't have beta access yet.`;
    } else who.hidden = true;
    $('betaSignOut').hidden = !me;
    $('betaId').textContent = me ? `Account #${me.id}` : '';
    // sign-in buttons, for people who aren't signed in (or want another account)
    const box = $('betaProviders'); box.replaceChildren();
    const providers = this.config.providers.filter(p => p !== 'guest');
    if (!me) {
      for (const p of providers) { const btn = document.createElement('button'); btn.type = 'button'; btn.className = 'btn provider ' + p; btn.textContent = `Sign in with ${cap(p)}`; btn.addEventListener('click', () => location.assign(`/auth/${p}/start`)); box.append(btn); }
      if (!providers.length) { const t = document.createElement('p'); t.className = 'hint'; t.textContent = 'Sign-in isn\'t set up on this server yet.'; box.append(t); }
    } else if (me.guest) {
      // a guest who wants to use an approved email: link a real sign-in to this account
      for (const p of providers) { const btn = document.createElement('button'); btn.type = 'button'; btn.className = 'btn provider ' + p; btn.textContent = `Link ${cap(p)}`; btn.addEventListener('click', () => location.assign(`/auth/${p}/start`)); box.append(btn); }
    }
    $('betaOr').hidden = !box.childElementCount;
    // the request, or where it stands
    const asked = b?.signup;
    $('betaForm').hidden = !!asked;
    const done = $('betaDone');
    if (asked) {
      done.hidden = false;
      done.textContent = asked.status === 'approved'
        ? `${asked.email} is approved. Sign in with that email to play.`
        : `You're on the list (${asked.email}, ${when(asked.at)}). We'll let you in soon.`;
    } else done.hidden = true;
    const email = $<HTMLInputElement>('betaEmail'); if (!email.value && b?.email) email.value = b.email;
    $('betaCodeBox').hidden = !this.config.betaCode;
  }

  private setupBeta(): void {
    $('betaSignOut').addEventListener('click', () => void this.signOut());
    $('betaForm').addEventListener('submit', async e => {
      e.preventDefault();
      const email = $<HTMLInputElement>('betaEmail').value.trim(), note = $<HTMLTextAreaElement>('betaNote').value.trim(), btn = $<HTMLButtonElement>('betaSend');
      btn.disabled = true;
      const r = await api<{ ok?: boolean; error?: string }>('/api/beta/signup', 'POST', { email, note }).catch(() => ({ status: 0, data: { error: 'Could not reach the server.' } }));
      btn.disabled = false;
      if (r.status !== 200) { $('betaErr').textContent = r.data.error || 'Could not send that. Try again.'; return; }
      $('betaErr').textContent = '';
      if (this.me) { await this.refreshMe(); this.showBeta(); return; }
      $('betaForm').hidden = true;
      const done = $('betaDone'); done.hidden = false;
      done.textContent = `Thanks! ${email} is on the list. When we let you in, sign in with that email to play.`;
    });
    $('betaCodeForm').addEventListener('submit', async e => {
      e.preventDefault();
      const code = $<HTMLInputElement>('betaCode').value.trim(); if (!code) return;
      const r = await api<{ ok?: boolean; error?: string }>('/api/beta/code', 'POST', { code }).catch(() => ({ status: 0, data: { error: 'Could not reach the server.' } }));
      if (r.status !== 200) { $('betaErr').textContent = r.data.error || 'That code doesn\'t work.'; return; }
      $<HTMLInputElement>('betaCode').value = '';
      await this.refreshMe(); await this.showChars();
    });
    // owners: the list of requests
    $('betaAdminBtn').addEventListener('click', () => void this.showBetaAdmin());
    $('betaAdminBack').addEventListener('click', () => void this.showChars());
    $('betaGrantForm').addEventListener('submit', async e => {
      e.preventDefault();
      const input = $<HTMLInputElement>('betaGrantEmail'), email = input.value.trim(); if (!email) return;
      const r = await api<{ error?: string }>('/api/beta/grant', 'POST', { email });
      if (r.status !== 200) { $('betaAdminErr').textContent = r.data.error || 'Could not add that email.'; return; }
      input.value = ''; await this.showBetaAdmin();
    });
  }

  private adminFilter: 'pending' | 'approved' | 'all' = 'pending';
  async showBetaAdmin(): Promise<void> {
    this.screen('betaAdmin');
    $('betaAdminErr').textContent = '';
    const r = await api<Signup[]>('/api/beta/signups');
    if (r.status !== 200 || !Array.isArray(r.data)) { $('betaAdminErr').textContent = 'Only owners can see this.'; return; }
    const all = r.data, count = (k: string) => k === 'all' ? all.length : all.filter(s => s.status === k).length;
    const filter = $('betaFilter'); filter.replaceChildren();
    for (const [k, label] of [['pending', 'Waiting'], ['approved', 'Approved'], ['all', 'All']] as const) {
      const b = document.createElement('button'); b.type = 'button'; b.className = this.adminFilter === k ? 'on' : ''; b.textContent = `${label} · ${count(k)}`;
      b.addEventListener('click', () => { this.adminFilter = k; void this.showBetaAdmin(); });
      filter.append(b);
    }
    const list = $('betaList'); list.replaceChildren();
    const rows = all.filter(s => this.adminFilter === 'all' || s.status === this.adminFilter);
    if (!rows.length) { const e = document.createElement('p'); e.className = 'empty'; e.textContent = this.adminFilter === 'pending' ? 'Nobody waiting right now.' : 'Nothing here yet.'; list.append(e); }
    for (const s of rows) {
      const row = document.createElement('div'); row.className = 'beta-row ' + s.status;
      const info = document.createElement('div');
      const em = document.createElement('div'); em.className = 'em'; em.textContent = s.email;
      const pill = document.createElement('span'); pill.className = 'pill'; pill.textContent = s.status === 'approved' ? 'Approved' : 'Waiting'; em.append(pill);
      const meta = document.createElement('div'); meta.className = 'meta';
      meta.textContent = `${when(s.at)}` + (s.accountId ? ` · account #${s.accountId}${s.label ? ' (' + s.label + ')' : ''}` : ' · not signed in yet');
      info.append(em, meta);
      const acts = document.createElement('div'); acts.className = 'acts';
      const act = (label: string, cls: string, fn: () => Promise<unknown>) => { const b = document.createElement('button'); b.type = 'button'; b.className = 'btn ' + cls; b.textContent = label; b.addEventListener('click', async () => { b.disabled = true; await fn(); await this.showBetaAdmin(); }); acts.append(b); };
      if (s.status === 'pending') act('Let in', 'primary', () => api(`/api/beta/signups/${s.id}/approve`, 'POST'));
      act(s.status === 'approved' ? 'Remove' : 'Dismiss', '', () => api(`/api/beta/signups/${s.id}`, 'DELETE'));
      row.append(info, acts);
      if (s.note) { const nt = document.createElement('div'); nt.className = 'nt'; nt.textContent = s.note; row.append(nt); }
      list.append(row);
    }
  }
}
